import { FC } from "react";

import {GetServerSideProps, InferGetServerSidePropsType as Infer} from "next";
import { useTranslation } from "next-i18next";

import LayoutHead from "@components/PageHeader/LayoutHead";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { loadFeatures } from "@modules/features";
import Layout, { TWithLayout } from "@modules/layout";
import {INotifications, loadNotifications} from "@modules/notifications";
import { loadAffiliateData, loadStaticOrderData, OrderPage, OrderStep } from "@modules/order";
import { QUERY_PARAMS } from "@utils/constants";
import getEnv from "@utils/getEnv";
import type {TStaticInitialOrderData} from "@modules/order/types";
import loadWeightRegulations from "@modules/weight-regulations/services/loadWeightRegulations";


type TWithLocales = {
    defaultLocale: string;
    customLocales: string[];
    customLocale: string; // Change from string? to string
    features: string[] | null;
    notifications: INotifications | null;
    orderData: TStaticInitialOrderData;
    shipmentId: string | null;
    weightRegulations: string[] | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales> = withPropsMiddleware(
  async ({
    locale,
    req: {
      headers: { host },
    },
    query,
  }) => {
    const shipmentId = (query[QUERY_PARAMS.ORDER_SHIPMENT_ID] as string) || null;

    const notificationsZone = getEnv("NEXT_PUBLIC_NOTIFICATION_ORDER_ZONE") as string;
    const notificationsPromise = locale ? loadNotifications(notificationsZone, locale, host!) : null;

    const affiliateId = query[QUERY_PARAMS.ORDER_AFFILIATE_ID] as string;
    const initialAffiliateData = affiliateId ? await loadAffiliateData(affiliateId, locale!, host!) : null;

    const orderDataPromise = loadStaticOrderData({ initialAffiliateData, locale: locale!, hostname: host! });

    const [notifications, orderData] = await Promise.all([notificationsPromise, orderDataPromise]);

    const features = locale ? await loadFeatures(locale, host) : null;

    const weightRegulations = locale? await loadWeightRegulations(locale, host) : null;

    return {
      props: { notifications, orderData, shipmentId, features, weightRegulations, defaultLocale: '', customLocales: [], customLocale: '' },
    };
  }
);

const OrderPackagePage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({
  notifications,
  orderData,
  shipmentId,
  weightRegulations
}) => {
  const { t } = useTranslation();

  return (
    <>
      <LayoutHead title={t("pages-order_package-title")} description={t("pages-order_package-description")} />
      <OrderPage
        currentStep={OrderStep.PackageInfo}
        notifications={notifications}
        orderData={orderData}
        weightRegulations={weightRegulations}
        shipmentId={shipmentId || undefined}
      />
    </>
  );
};

OrderPackagePage.Layout = <Layout hasMobileFooter={false} />;

export default OrderPackagePage;
